// frontend/src/components/dashboard/ClientDetailsDialog.js
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import axiosInstance from '../../utils/axios';
import { useTranslation } from 'react-i18next';

export default function ClientDetailsDialog({ open, onClose, client, onSave, specialistId }) {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        company: '',
        firstContact: new Date(),
        phone: '',
        whatsapp: '',
        telegram: '',
        instagram: '',
        email: '',
        website: '',
        address: '',
        status: 'new',
        projectType: '',
        projectDescription: '',
        technicalDetails: '',
        deadline: '',
        budget: '',
        projectStatus: 'discussion',
        isDeleted: false,
        specialistId: specialistId
    });

    useEffect(() => {
        if (client) {
            setFormData({
                ...client,
                firstContact: new Date(client.firstContact)
            });
        } else {
            resetForm();
        }
    }, [client, specialistId]);

    const resetForm = () => {
        setFormData({
            name: '',
            company: '',
            firstContact: new Date(),
            phone: '',
            whatsapp: '',
            telegram: '',
            instagram: '',
            email: '',
            website: '',
            address: '',
            status: 'new',
            projectType: '',
            projectDescription: '',
            technicalDetails: '',
            deadline: '',
            budget: '',
            projectStatus: 'discussion',
            isDeleted: false,
            specialistId: specialistId
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            let websiteUrl = formData.website;
            if (websiteUrl && !websiteUrl.match(/^https?:\/\//)) {
                websiteUrl = `https://${websiteUrl}`;
            }

            const dataToSave = {
                ...formData,
                website: websiteUrl
            };

            if (client) {
                await axiosInstance.patch(`/clients/${client._id}`, dataToSave);
            } else {
                await axiosInstance.post('/clients', dataToSave);
            }
            onSave();
            onClose();
        } catch (error) {
            console.error('Error saving client:', error);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md" 
            fullWidth
            aria-labelledby="client-dialog-title"
        >
            <DialogTitle id="client-dialog-title">
                {t(client ? 'common.editclient' : 'common.newclient')}
            </DialogTitle>
            <DialogContent>
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('common.namecompany')}
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                required
                                tabIndex={0}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DatePicker
                                label={t('common.firstcontactdate')}
                                value={formData.firstContact}
                                onChange={(newValue) => setFormData({...formData, firstContact: newValue})}
                                renderInput={(params) => <TextField {...params} fullWidth tabIndex={0} />}
                            />
                        </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label={t('common.phone')}
                            value={formData.phone}
                            onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="WhatsApp"
                            value={formData.whatsapp}
                            onChange={(e) => setFormData({...formData, whatsapp: e.target.value})}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Telegram"
                            value={formData.telegram}
                            onChange={(e) => setFormData({...formData, telegram: e.target.value})}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Instagram"
                            value={formData.instagram}
                            onChange={(e) => setFormData({...formData, instagram: e.target.value})}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('common.website')}
                            value={formData.website}
                            onChange={(e) => setFormData({...formData, website: e.target.value})}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('common.address')}
                            multiline
                            rows={2}
                            value={formData.address}
                            onChange={(e) => setFormData({...formData, address: e.target.value})}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel>{t('common.clientstatus')}</InputLabel>
                            <Select
                                value={formData.status}
                                label={t('common.clientstatus')}
                                onChange={(e) => setFormData({...formData, status: e.target.value})}
                            >
                                <MenuItem value="new">{t('common.new')}</MenuItem>
                                <MenuItem value="regular">{t('common.regular')}</MenuItem>
                                <MenuItem value="former">{t('common.former')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label={t('common.projecttype')}
                            value={formData.projectType}
                            onChange={(e) => setFormData({...formData, projectType: e.target.value})}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('common.projectdescription')}
                            multiline
                            rows={3}
                            value={formData.projectDescription}
                            onChange={(e) => setFormData({...formData, projectDescription: e.target.value})}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('common.technicaldetails')}
                            multiline
                            rows={2}
                            value={formData.technicalDetails}
                            onChange={(e) => setFormData({...formData, technicalDetails: e.target.value})}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label={t('common.budget')}
                            value={formData.budget}
                            onChange={(e) => setFormData({...formData, budget: e.target.value})}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
    <InputLabel>{t('common.projectstatus')}</InputLabel>
    <Select
        value={formData.projectStatus}
        label={t('common.projectstatus')}
        onChange={(e) => setFormData({...formData, projectStatus: e.target.value})}
    >
        <MenuItem value="discussion">{t('common.discussion')}</MenuItem>
        <MenuItem value="contacted">{t('common.contacted')}</MenuItem>
        <MenuItem value="unreachable">{t('common.unreachable')}</MenuItem>
        <MenuItem value="negotiating">{t('common.negotiating')}</MenuItem>
        <MenuItem value="meeting">{t('common.meeting')}</MenuItem>
        <MenuItem value="development">{t('common.development')}</MenuItem>
        <MenuItem value="completed">{t('common.completed')}</MenuItem>
    </Select>
</FormControl>
                    </Grid>
                </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} tabIndex={0}>{t('common.cancel')}</Button>
                <Button 
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    tabIndex={0}
                >
                    {t(client ? 'common.savechanges' : 'common.createclient')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}